.base,
.base *,
.base *::before,
.base *::after {
  box-sizing: border-box;
}

.base {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  width: 100%;

  --input-height: var(--control-height-md);
  --input-font: var(--text-sm);
  --input-icon-inset: var(--space-8);
}

.field {
  position: relative;
  display: flex;
  align-items: center;
}

.input {
  width: 100%;
  height: var(--input-height);
  padding: 0 var(--space-3);

  font-family: var(--font-sans);
  font-size: var(--input-font);
  font-weight: var(--weight-regular);
  color: var(--input-text);

  background: var(--input-bg);
  border: 1px solid var(--input-border);
  border-radius: var(--radius-md);
  outline: none;

  transition:
    border-color var(--duration-fast) var(--ease-default),
    box-shadow var(--duration-fast) var(--ease-default),
    background-color var(--duration-fast) var(--ease-default);
}

.input::placeholder {
  color: var(--input-placeholder);
}

.input:hover:not(:disabled) {
  background: var(--input-bg-hover);
}

/* The one focus treatment in the library that isn't the 2px ring, on purpose: a
   field already has a border, so focus recolours that and adds a halo. The halo
   also carries status — see the error/success/warning rules below — which a single
   gold ring couldn't. Controls without a border of their own get the ring. */
.input:focus-visible {
  border-color: var(--input-border-focus);
  background: var(--input-bg-focus);
  box-shadow: 0 0 0 3px var(--active-muted);
}

.input:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ── Sizes ── */

.sm {
  --input-height: var(--control-height-sm);
  --input-font: var(--text-xs);
  --input-icon-inset: var(--space-6);
}

.md {
  --input-height: var(--control-height-md);
  --input-font: var(--text-sm);
  --input-icon-inset: var(--space-8);
}

.lg {
  --input-height: var(--control-height-lg);
  --input-font: var(--text-base);
  --input-icon-inset: var(--space-10);
}

/* ── Label ── */

.label {
  font-size: var(--text-xs);
  font-weight: var(--weight-semi);
  font-family: var(--font-sans);
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: var(--tracking-caps);
}

/* ── Icon slots ── */

.hasIconLeft .input {
  padding-left: var(--input-icon-inset);
}

.hasIconRight .input {
  padding-right: var(--input-icon-inset);
}

.iconLeft,
.iconRight {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  color: var(--text-muted);
  pointer-events: none;
}

.iconLeft {
  left: var(--space-2);
}

.iconRight {
  right: var(--space-2);
}

/* ── Clear button ── */

.clear {
  position: absolute;
  right: var(--space-2);
  top: 50%;
  transform: translateY(-50%);

  display: flex;
  align-items: center;
  padding: var(--space-1);

  color: var(--text-muted);
  background: none;
  border: none;
  border-radius: var(--radius-sm);
  cursor: pointer;

  transition: color var(--duration-fast) var(--ease-default);
}

.clear:hover {
  color: var(--text-primary);
}

.clear:focus-visible {
  outline: var(--focus-ring);
  outline-offset: var(--focus-ring-offset);
}

/* ── Status ── */

.error .input {
  border-color: var(--color-danger);
}

.error .input:focus-visible {
  box-shadow: 0 0 0 3px var(--color-danger-muted);
}

.success .input {
  border-color: var(--color-success);
}

.success .input:focus-visible {
  box-shadow: 0 0 0 3px var(--color-success-muted);
}

.warning .input {
  border-color: var(--color-warning);
}

.warning .input:focus-visible {
  box-shadow: 0 0 0 3px var(--color-warning-muted);
}

/* ── Message ── */

.message {
  margin: 0;
  font-family: var(--font-sans);
  font-size: var(--text-xs);
  line-height: var(--leading-tight);
  color: var(--text-muted);
}

.error .message {
  color: var(--color-danger);
}

.success .message {
  color: var(--color-success);
}

.warning .message {
  color: var(--color-warning);
}

@media (prefers-reduced-motion: reduce) {
  .input,
  .clear {
    transition: none;
  }
}
